import {AxiosError} from "axios";
import {SubscriptionDTO} from "../Models/DTOs/Subscription";
import {IsFreeAccount} from "./Account/Admin";

export function IsSubscriptionActive(subStatus: string | null | undefined): boolean {
    switch (subStatus) {
        case "canceled":
        case "none":
        case "paused":
        case "incomplete":
        case "incomplete_expired":
        case "unpaid":
        case "past_due":
        case null:
        case undefined:
            return false;
        default:
            return true;
    }
}

export function IsSubscriptionNegativeStatus(subStatus: string | null | undefined): boolean {
    switch (subStatus) {
        case "canceled":
        case "none":
        case "incomplete":
        case "incomplete_expired":
        case "past_due":
        case null:
        case undefined:
            return true;
        default:
            return false;
    }
}

export function SubscriptionStatusColor(
    subStatus: string | null | undefined
): string {
    switch (subStatus) {
        case "canceled":
        case "incomplete_expired":
        case "unpaid":
        case "past_due":
            return "#c47575";

        case "active":
        case "trialing":
            return "#77c98e";

        case "paused":
            return "#8fb3df";

        case "none":
        case null:
        case undefined:
            return "#6b7280";

        default:
            return "#c9bf6b";
    }
}

export function SubscriptionStatusAccentColor(
    subStatus: string | null | undefined
): string {
    switch (subStatus) {
        case "canceled":
        case "incomplete_expired":
        case "unpaid":
        case "past_due":
            return "#700000";

        case "active":
        case "trialing":
            return "#005410";

        case "paused":
            return "#1d3a63";

        case "none":
        case null:
        case undefined:
            return "#3a3c42";

        default:
            return "#5b4b00";
    }
}

export function HasLifetimeMembership(PlanId: number | null | undefined) {
    return PlanId && PlanId === 3;
}

export function CanAccessMemberOnlyContent(status?: string, plan_id?: number, admin_role_id?: number, isEntitled?: boolean) {
    // is_entitled is the auth service's grace-aware source of truth — it stays true
    // for a paused subscription that's still inside its paid-through grace window
    // (a scheduled pause). Prefer it; keep the status / lifetime / free fallbacks so
    // access never regresses if a response omits the flag.
    return !!isEntitled || IsSubscriptionActive(status) || HasLifetimeMembership(plan_id) || IsFreeAccount(admin_role_id);
}

export function IsUsingIntroPeriod(sub: SubscriptionDTO): boolean {
    return sub.LastInvoiceTotalAmount !== undefined && sub.LastInvoiceTotalAmount !== null && sub.LastInvoiceTotalAmount !== sub.PriceUnitAmount;
}

export function normalizeStripeUpgradeError(err: unknown): Error {
    const ax = err as AxiosError;
    const code = ax.response?.status;
    const text = (ax.response?.data as string) || ax.message;

    // Map server’s plain-string errors to nicer messages
    const map: Record<string, string> = {
        no_active_subscription: "You don’t have an active subscription.",
        already_yearly: "You’re already on the yearly plan.",
        no_default_payment_method:
            "Add a default card to your account before upgrading.",
        unsupported_cadence_for_upgrade:
            "This upgrade path isn’t supported from your current plan.",
        missing_yearly_price:
            "Yearly price is not configured. Contact support.",
        stripe_preview_failed:
            "Couldn’t generate the proration preview. Try again.",
        stripe_upgrade_failed:
            "Stripe refused the upgrade. Update your card and retry."
    };

    const msg = map[text] || `${code ?? ""} ${text}`.trim();
    return new Error(msg);
}

// -----------------------------------------------------------------------------
// Pause-billing helpers
// -----------------------------------------------------------------------------

// Backend pause timestamps are RFC3339 UTC (e.g. "2026-09-15T00:00:00Z"). Date
// parsing across the app is inconsistent about the trailing "Z", so be defensive:
// if the string carries no timezone designator, treat it as UTC.
function parseUtcDate(value: string): Date {
    const hasZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(value);
    return new Date(hasZone ? value : value + "Z");
}

/**
 * Single source of truth for whether a subscription grants premium content
 * access right now. Mirrors the server-computed SubscriptionDTO.HasContentAccess
 * so long client sessions can recompute it without a re-fetch.
 */
export function hasContentAccess(
    sub: SubscriptionDTO | null | undefined,
    now: Date = new Date()
): boolean {
    if (!sub) return false;
    if (HasLifetimeMembership(sub.PlanId)) return true; // lifetime
    if (sub.Status === "active" || sub.Status === "trialing" || sub.Status === "past_due") return true;
    if (sub.Status === "paused") {
        // Resumed already → access is back.
        if (sub.PauseResumesAtUtc && now >= parseUtcDate(sub.PauseResumesAtUtc)) return true;
        // Grace window (paid-through) → still has access; otherwise frozen.
        return !!sub.PauseAccessUntilUtc && now < parseUtcDate(sub.PauseAccessUntilUtc);
    }
    return false;
}

/**
 * Whether the "Pause" option should be offered. The backend is the final
 * authority (it returns a specific error if any condition fails), but this
 * pre-check hides the option for the obvious cases.
 */
export function canPauseSubscription(
    sub: SubscriptionDTO | null | undefined,
    now: Date = new Date()
): boolean {
    if (!sub) return false;
    const base =
        sub.ProviderId === 1 &&            // Stripe only
        sub.Status === "active" &&
        sub.IsPaused !== true &&           // not already paused (tolerate an omitted flag; Status==="active" already excludes paused)
        sub.PlanId !== 3 &&                // not lifetime
        sub.CancelAtUtc == null;           // no pending cancellation
    if (!base || (sub.BillingIntervalCount ?? 1) !== 1) return false;
    // Monthly: pausable any time. Yearly: only in the final month before renewal (the server is the
    // authority and re-anchors billing on resume). Other cadences: unsupported.
    if (sub.BillingInterval === "month") return true;
    if (sub.BillingInterval === "year") return isYearlyInFinalMonth(sub, now);
    return false;
}

/**
 * Whether a yearly subscription is within the last calendar month before its renewal
 * (CurrentPeriodEndUtc). Mirrors the server's withinFinalMonth gate; the server is authoritative.
 */
export function isYearlyInFinalMonth(
    sub: SubscriptionDTO | null | undefined,
    now: Date = new Date()
): boolean {
    if (!sub?.CurrentPeriodEndUtc) return false;
    const cpe = parseUtcDate(sub.CurrentPeriodEndUtc);
    const windowStart = new Date(cpe);
    windowStart.setUTCMonth(windowStart.getUTCMonth() - 1); // UTC, to mirror the server's AddDate gate
    return now >= windowStart && now < cpe;
}

/**
 * True when resuming a paused subscription will charge the member immediately —
 * i.e. they're in the freeze window (past the paid-through grace boundary).
 * Used to pre-warn the user before they confirm an early resume.
 */
export function resumeChargesImmediately(
    sub: SubscriptionDTO | null | undefined,
    now: Date = new Date()
): boolean {
    // Treat Status==='paused' as paused too (matches the rest of the feature),
    // so the charge warning still fires if a response omits the IsPaused flag.
    const paused = !!sub?.IsPaused || sub?.Status === "paused";
    if (!sub || !paused) return false;
    return !!sub.PauseAccessUntilUtc && now >= parseUtcDate(sub.PauseAccessUntilUtc);
}

/**
 * Maps the plain-text error tokens returned by the pause/unpause endpoints to
 * user-facing copy. Falls back to a generic message for anything unmapped.
 */
export function normalizePauseError(err: unknown): string {
    const ax = err as AxiosError;
    const text =
        typeof ax?.response?.data === "string"
            ? (ax.response.data as string).trim()
            : ax?.message ?? "";

    const map: Record<string, string> = {
        invalid_duration_months: "Please choose a pause length of 1, 3, or 6 months.",
        not_pausable_status: "Only active subscriptions can be paused.",
        lifetime_not_pausable: "Lifetime memberships can’t be paused.",
        pause_unsupported_for_billing_interval: "Pausing isn’t available on this plan.",
        pause_yearly_only_last_month: "Annual members can pause only in the final month before renewal.",
        already_paused: "Your membership is already paused.",
        cancel_pending: "Please resume your scheduled cancellation before pausing.",
        no_active_subscription: "No active subscription found.",
        not_paused: "Your membership isn’t currently paused.",
        stripe_fetch_failed: "Something went wrong, please try again.",
        stripe_pause_failed: "Something went wrong, please try again.",
        stripe_unpause_failed: "Something went wrong, please try again.",
        missing_access_token: "Your session has expired. Please sign in again.",
        invalid_access_token: "Your session has expired. Please sign in again.",
        forbidden: "You don’t have permission to do that.",
    };

    return map[text] || "Something went wrong, please try again.";
}

export function isUsingIntroPrice(sub: SubscriptionDTO): boolean {
    if (
        sub.LastInvoiceTotalAmount == null ||
        HasLifetimeMembership(sub.PlanId)
    ) {
        return false;
    }

    // amounts must differ
    if (sub.LastInvoiceTotalAmount === sub.PriceUnitAmount) {
        return false;
    }

    // if we have dates, make sure the last invoice date is not *before* the current period
    // if (sub.LastInvoiceCreatedUtc && sub.CurrentPeriodStartUtc) {
    //     const invoiceDate = new Date(sub.LastInvoiceCreatedUtc);
    //     const periodStart = new Date(sub.CurrentPeriodStartUtc);
    //
    //     // if invoice is older than current period, ignore it (old plan)
    //     if (invoiceDate < periodStart) {
    //         return false;
    //     }
    // }

    return true;
}
